import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Camera, Image as ImageIcon, X, Eye, Paperclip, Loader2 } from 'lucide-react';

/**
 * ReceiptUploader - A component for uploading receipts with camera/gallery options
 * Supports multiple files (max 5) and mobile-friendly camera capture
 */
const ReceiptUploader = ({
  receipts = [], // Array of {id, filename, original_name} for existing receipts
  pendingFiles = [], // Array of File objects for new uploads
  onFilesChange, // Callback when pending files change
  onDeleteReceipt, // Callback to delete existing receipt (receipt_id) => void
  onViewReceipt, // Callback to view existing receipt (receipt) => void
  maxReceipts = 5,
  disabled = false,
  compact = false // For batch entry mode
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileDevice || isTouchDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const totalCount = receipts.length + pendingFiles.length;
  const canAddMore = totalCount < maxReceipts;

  const handleCameraCapture = () => {
    if (cameraInputRef.current && canAddMore) {
      cameraInputRef.current.click();
    }
  };

  const handleGallerySelect = () => {
    if (galleryInputRef.current && canAddMore) {
      galleryInputRef.current.click();
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Check how many more files we can add
    const remainingSlots = maxReceipts - totalCount;
    const filesToAdd = files.slice(0, remainingSlots);

    // Validate file types
    const validFiles = filesToAdd.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length > 0) {
      onFilesChange([...pendingFiles, ...validFiles]);
    }

    // Reset input
    event.target.value = '';
  };

  const handleRemovePending = (index) => {
    const updated = pendingFiles.filter((_, i) => i !== index);
    onFilesChange(updated);
  };

  if (compact) {
    // Compact mode for batch entry - single row display
    return (
      <div className="space-y-2">
        {/* Existing receipts */}
        {receipts.map((receipt, index) => (
          <div key={receipt.id || index} className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs">
            <Paperclip className="h-3 w-3 text-green-600 flex-shrink-0" />
            <span className="text-green-700 truncate flex-1" title={receipt.original_name}>
              {receipt.original_name}
            </span>
            {onViewReceipt && (
              <button
                type="button"
                onClick={() => onViewReceipt(receipt)}
                className="text-green-600 hover:text-green-800"
                title={t('auto.misc2.view2')}
              >
                <Eye className="h-3 w-3" />
              </button>
            )}
            {onDeleteReceipt && (
              <button
                type="button"
                onClick={() => onDeleteReceipt(receipt.id)}
                className="text-red-500 hover:text-red-700"
                title={t('auto.misc2.remove')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {/* Pending files */}
        {pendingFiles.map((file, index) => (
          <div key={`pending-${index}`} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
            <Paperclip className="h-3 w-3 text-blue-600 flex-shrink-0" />
            <span className="text-blue-700 truncate flex-1" title={file.name}>
              {file.name}
            </span>
            <button
              type="button"
              onClick={() => handleRemovePending(index)}
              className="text-red-500 hover:text-red-700"
              title={t('auto.misc2.remove')}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}

        {/* Add buttons */}
        {canAddMore && !disabled && (
          <div className="flex gap-2">
            {isMobile ? (
              <>
                <button
                  type="button"
                  onClick={handleCameraCapture}
                  className="flex items-center gap-1 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs hover:bg-gray-200"
                >
                  <Camera className="h-3 w-3" />
                  <span>{t('receipts.camera')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleGallerySelect}
                  className="flex items-center gap-1 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs hover:bg-gray-200"
                >
                  <ImageIcon className="h-3 w-3" />
                  <span>{t('receipts.gallery')}</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleGallerySelect}
                className="flex items-center gap-1 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs hover:bg-gray-200"
              >
                <Paperclip className="h-3 w-3" />
                <span>{t('receipts.add')}</span>
              </button>
            )}
            <span className="text-xs text-gray-400 self-center">
              ({totalCount}/{maxReceipts})
            </span>
          </div>
        )}

        {/* Hidden inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept=".pdf,image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  // Full mode for single expense dialog
  return (
    <div className="space-y-3">
      {/* Label and count */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {t('receipts.count', { count: totalCount, max: maxReceipts })}
        </span>
        {totalCount >= maxReceipts && (
          <span className="text-xs text-amber-600">{t('receipts.maxReached')}</span>
        )}
      </div>

      {/* Existing receipts */}
      {receipts.length > 0 && (
        <div className="space-y-2">
          {receipts.map((receipt, index) => (
            <div 
              key={receipt.id || index} 
              className="flex items-center gap-3 px-3 py-2 bg-green-50 border border-green-200 rounded-md"
            >
              <Paperclip className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700 truncate flex-1" title={receipt.original_name}>
                {receipt.original_name}
              </span>
              <div className="flex items-center gap-1">
                {onViewReceipt && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewReceipt(receipt)}
                    title={t('auto.misc2.viewReceipt')}
                    className="h-7 w-7 p-0"
                  >
                    <Eye className="h-4 w-4 text-green-600" />
                  </Button>
                )}
                {onDeleteReceipt && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteReceipt(receipt.id)}
                    title={t('auto.misc2.deleteReceipt')}
                    className="h-7 w-7 p-0"
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending files to upload */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          {pendingFiles.map((file, index) => (
            <div 
              key={`pending-${index}`} 
              className="flex items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md"
            >
              <Paperclip className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-700 truncate flex-1" title={file.name}>
                {file.name}
                <span className="text-xs text-blue-500 ml-2">{t('auto.misc2.pendingUpload')}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemovePending(index)}
                title={t('auto.misc2.remove')}
                className="h-7 w-7 p-0"
              >
                <X className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Upload buttons */}
      {canAddMore && !disabled && (
        <div className="flex flex-wrap gap-2">
          {isMobile ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCameraCapture}
                className="flex items-center gap-2"
               data-testid="btn-cameracapture">
                <Camera className="h-4 w-4" />
                {t('receipts.takePhoto')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGallerySelect}
                className="flex items-center gap-2"
               data-testid="btn-galleryselect">
                <ImageIcon className="h-4 w-4" />
                {t('receipts.fromGallery')}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGallerySelect}
              className="flex items-center gap-2"
             data-testid="btn-galleryselect">
              <Paperclip className="h-4 w-4" />
              {t('receipts.addPdfOrImage')}
            </Button>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept=".pdf,image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help text */}
      {totalCount === 0 && (
        <p className="text-xs text-gray-500">
          {t('receipts.hint')}
        </p>
      )}
    </div>
  );
};

export default ReceiptUploader;
